import React, {useState, useMemo} from 'react';
import {MediaFile} from '../types';
import MediaViewer from './MediaViewer';
import Alert from './ErrorDisplay';

interface GalleryPageProps {
    media: MediaFile[];
}

// ImageButton component
const ImageButton: React.FC<{image: MediaFile; onOpen: (item: MediaFile) => void}> = ({image, onOpen}) => {
    const aspectRatio = image.width && image.height ? image.height / image.width : 1;
    
    return (
        <button
            onClick={() => onOpen(image)}
            className="relative w-full overflow-hidden rounded-lg bg-gray-100 hover:opacity-90 active:scale-95 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-pink-500 shadow-sm"
            aria-label={image.caption || image.filename}
            style={{ aspectRatio: `1 / ${aspectRatio}` }}
        >
            <img
                src={image.thumbnailUrl || image.url}
                alt={image.caption || image.filename}
                className="w-full h-full object-cover"
                loading="lazy"
            />
            {image.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/60 to-transparent px-2 py-1">
                    <p className="text-white text-xs truncate">{image.caption}</p>
                </div>
            )}
        </button>
    );
};

type BalancedMediaFile = MediaFile & {
    normalizedHeight: number;
};

function balanceStacks(stacks: MediaFile[][]): MediaFile[][] {
    const balancedStacks: BalancedMediaFile[][] = stacks.map((stack) => stack.map((item) => ({
        ...item,
        normalizedHeight: item.width ? item.height * 100 / item.width : 100,
    })));

    const getHeight = (stack: BalancedMediaFile[]) => stack.reduce((sum, item) => sum + item.normalizedHeight, 0);
    const totalHeight = balancedStacks.reduce((sum, stack) => sum + getHeight(stack), 0);
    const targetHeight = totalHeight / balancedStacks.length;

    let changed = true;
    while (changed) {
        changed = false;

        const heights = balancedStacks.map(getHeight);
        const maxIdx = heights.indexOf(Math.max(...heights));
        const minIdx = heights.indexOf(Math.min(...heights));

        if (Math.abs(heights[maxIdx] - heights[minIdx]) < 1) break;

        const item = balancedStacks[maxIdx].pop();
        if (!item) break;

        const newMaxHeight = heights[maxIdx] - item.normalizedHeight;
        const newMinHeight = heights[minIdx] + item.normalizedHeight;

        if (
            Math.abs(newMaxHeight - targetHeight) < Math.abs(heights[maxIdx] - targetHeight) &&
            Math.abs(newMinHeight - targetHeight) < Math.abs(heights[minIdx] - targetHeight)
        ) {
            balancedStacks[minIdx].push(item);
            changed = true;
        } else {
            balancedStacks[maxIdx].push(item);
            break;
        }
    }

    return balancedStacks.map((stack) => stack.map(({normalizedHeight, ...item}) => item));
}

function buildImageColumns(images: MediaFile[], columnCount: number): MediaFile[][] {
    const columns = Array.from({length: columnCount}, () => [] as MediaFile[]);

    for (let i = 0; i < images.length; i++) {
        columns[i % columnCount].push(images[i]);
    }

    return balanceStacks(columns);
}

const GalleryPage: React.FC<GalleryPageProps> = ({media}) => {
    const [viewerIndex, setViewerIndex] = useState<number | null>(null);

    const safeMedia = media ?? [];

    const {images, videos} = useMemo(() => {
        const nextImages: MediaFile[] = [];
        const nextVideos: MediaFile[] = [];

        for (const item of safeMedia) {
            if (item.mimetype.startsWith('image/')) {
                nextImages.push(item);
            } else if (item.mimetype.startsWith('video/')) {
                nextVideos.push(item);
            }
        }

        return {
            images: nextImages,
            videos: nextVideos,
        };
    }, [safeMedia]);

    const {
        imageColumns2,
        imageColumns3,
        imageColumns4,
        imageColumns5,
    } = useMemo(() => ({
        imageColumns2: buildImageColumns(images, 2),
        imageColumns3: buildImageColumns(images, 3),
        imageColumns4: buildImageColumns(images, 4),
        imageColumns5: buildImageColumns(images, 5),
    }), [images]);

    // Build a single ordered list: images first, then videos — used by the viewer
    const allMedia = useMemo(() => [...images, ...videos], [images, videos]);

    const openViewer = (item: MediaFile) => {
        const idx = allMedia.findIndex(m => m.id === item.id);
        setViewerIndex(idx >= 0 ? idx : 0);
    };

    if (safeMedia.length === 0) {
        return (
            <Alert
                title="Keine Medien"
                message="Es wurden noch keine Fotos oder Videos hochgeladen."
                type="info"
            />
        );
    }

    return (
        <div className="space-y-6">
            {/* Images Section */}
            {images.length > 0 && (
                <section>
                    <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-pink-600" style={{fontSize: '1.25rem'}}>
                            photo_library
                        </span>
                        Fotos
                        <span className="text-sm font-normal text-gray-400">({images.length})</span>
                    </h2>
                    
                    {/* Mobile - 2 columns */}
                    <div className="block sm:hidden">
                        <div className="grid grid-cols-2 gap-2">
                            {imageColumns2.map((column, colIdx) => (
                                <div key={colIdx} className="flex flex-col gap-2">
                                    {column.map(image => (
                                        <ImageButton key={image.id} image={image} onOpen={openViewer} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Small screens - 3 columns */}
                    <div className="hidden sm:block md:hidden">
                        <div className="grid grid-cols-3 gap-2">
                            {imageColumns3.map((column, colIdx) => (
                                <div key={colIdx} className="flex flex-col gap-2">
                                    {column.map(image => (
                                        <ImageButton key={image.id} image={image} onOpen={openViewer} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Medium screens - 4 columns */}
                    <div className="hidden md:block lg:hidden">
                        <div className="grid grid-cols-4 gap-2">
                            {imageColumns4.map((column, colIdx) => (
                                <div key={colIdx} className="flex flex-col gap-2">
                                    {column.map(image => (
                                        <ImageButton key={image.id} image={image} onOpen={openViewer} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Large screens - 5 columns */}
                    <div className="hidden lg:block">
                        <div className="grid grid-cols-5 gap-2">
                            {imageColumns5.map((column, colIdx) => (
                                <div key={colIdx} className="flex flex-col gap-2">
                                    {column.map(image => (
                                        <ImageButton key={image.id} image={image} onOpen={openViewer} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Videos Section */}
            {videos.length > 0 && (
                <section>
                    <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-pink-600" style={{ fontSize: '1.25rem' }}>
                            video_library
                        </span>
                        Videos
                        <span className="text-sm font-normal text-gray-400">({videos.length})</span>
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                        {videos.map(video => {
                            const aspectRatio = video.width && video.height ? video.height / video.width : 9 / 16;
                            return (
                                <button
                                    key={video.id}
                                    onClick={() => openViewer(video)}
                                    className="relative w-full overflow-hidden rounded-lg bg-gray-900 hover:opacity-90 active:scale-95 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-pink-500 shadow-sm"
                                    aria-label={video.caption || video.filename}
                                    style={{ aspectRatio: `1 / ${aspectRatio}` }}
                                >
                                    {video.thumbnailUrl ? (
                                        <img
                                            src={video.thumbnailUrl}
                                            alt={video.caption || video.filename}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                            <span className="material-symbols-outlined text-white/40" style={{ fontSize: '3rem' }}>
                                                movie
                                            </span>
                                        </div>
                                    )}
                                    {/* Play overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20">
                                            <span className="material-symbols-outlined text-white" style={{ fontSize: '2rem' }}>
                                                play_arrow
                                            </span>
                                        </div>
                                    </div>
                                    {video.caption && (
                                        <div className="px-3 py-2 bg-gray-900 text-left">
                                            <p className="text-white/80 text-sm truncate">{video.caption}</p>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Unified Media Viewer with swipe support */}
            {viewerIndex !== null && (
                <MediaViewer
                    media={allMedia}
                    startIndex={viewerIndex}
                    onClose={() => setViewerIndex(null)}
                />
            )}
        </div>
    );
};

export default GalleryPage;

